import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { bookkeeperInfoSchema } from "../../yup";
import { useUpdateBookkeeperMutation } from "../../services/api";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store/store";
import { updatePurlUser } from "../../store/reducers/purlUserReducer";
import { IoIosCloseCircle } from "react-icons/io";
import Loader from "../Loader";
import { tracker } from "../../services/mixpanel";

interface BookkeeperFieldValues {
  bookkeeperName: string;
  bookkeeperEmail: string;
}

interface BookkeeperFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialValues?: Partial<BookkeeperFieldValues>;
}

export const BookkeeperForm = ({
  isOpen,
  onClose,
  initialValues,
}: BookkeeperFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BookkeeperFieldValues>({
    resolver: yupResolver(bookkeeperInfoSchema),
    defaultValues: initialValues,
    mode: "onChange",
  });

  const [updateBookkeeper, { isLoading: bookkeeperLoading }] =
    useUpdateBookkeeperMutation();
  const loginData = useSelector((state: RootState) => state.login);
  const dispatch = useAppDispatch();

  const submitBookkeeper: SubmitHandler<BookkeeperFieldValues> = async (
    values
  ) => {
    tracker.dashboard.bookkeeper.formSubmit(!!initialValues?.bookkeeperEmail);
    try {
      const sendData = {
        crm_Contact_ID: loginData.zohoCustomerId,
        bookkeeperName: values.bookkeeperName,
        bookkeeperEmail: values.bookkeeperEmail,
      };

      const result = await updateBookkeeper(sendData).unwrap();
      if (result.data.code !== "success") throw new Error(result?.message);

      dispatch(
        updatePurlUser({
          bookkeeperName: values.bookkeeperName,
          bookkeeperEmail: values.bookkeeperEmail,
        })
      );

      onClose();
    } catch (err) {
      console.log("err in updating bookkeeper", err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="w-full h-full fixed flex justify-center items-center bg-opacity-40 bg-black z-10">
      <form
        onSubmit={handleSubmit(submitBookkeeper)}
        className="w-1/3 min-w-[300px] bg-[#CFD8E4] p-8 rounded-xl text-center relative"
      >
        <IoIosCloseCircle
          onClick={onClose}
          className="absolute right-2 top-2 text-2xl cursor-pointer"
        />
        <div className="flex flex-col w-full mb-4">
          <label className="text-black font-semibold text-start">
            Bookkeeper Name
          </label>
          <input
            {...register("bookkeeperName")}
            className="p-2 text-[17px] rounded-md w-full"
          />
          {errors.bookkeeperName && (
            <p className="text-red-500 text-start text-sm mt-1">
              {errors.bookkeeperName.message}
            </p>
          )}
        </div>
        <div className="flex flex-col w-full mb-4">
          <label className="text-black font-semibold text-start">
            Bookkeeper Email
          </label>
          <input
            {...register("bookkeeperEmail")}
            className="p-2 text-[17px] rounded-md w-full"
          />
          {errors.bookkeeperEmail && (
            <p className="text-red-500 text-start text-sm mt-1">
              {errors.bookkeeperEmail.message}
            </p>
          )}
        </div>
        <button className="h-[32px] px-6 rounded-sm bg-[#356C58] text-white">
          {bookkeeperLoading ? <Loader isLoading={true} size={20} /> : "SUBMIT"}
        </button>
      </form>
    </div>
  );
};
